import React, { useEffect, useState } from "react";
import axios from "axios";
import { MemoAssist } from "@/components/memo/MemoAssist";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/re`;
const when = (v) => new Date(v).toLocaleString();

const PortalMemo = ({ m, token, onDone }) => {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const send = async () => {
    setBusy(true); setErr(null);
    try { await axios.post(`${API}/portal/memos/${m.id}/replies`, { body: reply }, { params: { token } }); setReply(""); onDone(); }
    catch (e) { setErr(e?.response?.status === 409 ? "This message was closed by staff." : "Couldn't send your reply. Please try again."); onDone(); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`portal-memo-${m.id}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-2">
      <div className="text-sm font-semibold">{m.subject}</div>
      <div className="text-xs text-white/50">{m.from_kind === "resident" ? `You → ${m.to_staff?.name}` : m.author?.name} · {when(m.created_at)}</div>
      <p className="text-sm text-white/80 whitespace-pre-wrap">{m.body}</p>
      {(m.replies || []).map((r) => (
        <p key={r.id} className={`text-xs pl-3 border-l ${r.by_kind === "resident" ? "border-emerald-400/50 text-emerald-100/80" : "border-amber-300/40 text-white/60"}`}>
          {r.by_kind === "resident" ? "You" : r.author?.name} · {when(r.at)}<span className="block mt-0.5 whitespace-pre-wrap leading-relaxed">{r.body}</span>
        </p>
      ))}
      <div className="flex gap-2 pt-1">
        <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" data-testid={`portal-memo-reply-${m.id}`}
          className="flex-1 rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-amber-300/50" />
        <button type="button" disabled={busy || !reply.trim()} onClick={send} data-testid={`portal-memo-reply-send-${m.id}`}
          className="rounded-full bg-amber-300 text-black px-4 text-sm font-semibold disabled:opacity-40 transition-opacity">Reply</button>
      </div>
      <MemoAssist value={reply} onChange={setReply} id={`portal-memo-reply-${m.id}`} />
      {err && <p data-testid={`portal-memo-error-${m.id}`} className="text-xs text-red-300">{err}</p>}
    </div>
  );
};

const field = "w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:border-amber-300/50";

function NewMemo({ token, onSent }) {
  const [staff, setStaff] = useState([]);
  const [f, setF] = useState({ staff_id: "", subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => { axios.get(`${API}/portal/memo-staff`, { params: { token } }).then((r) => setStaff(r.data.staff)).catch(() => {}); }, [token]);
  const send = async () => {
    setBusy(true); setMsg(null);
    try { await axios.post(`${API}/portal/memos`, f, { params: { token } }); setF({ staff_id: "", subject: "", body: "" }); setMsg({ ok: true, t: "Sent. Your staff member has been notified." }); onSent(); }
    catch (e) { setMsg({ ok: false, t: e?.response?.data?.detail || "Couldn't send. Please try again." }); }
    finally { setBusy(false); }
  };
  return (
    <section data-testid="portal-memo-new" className="rounded-xl border border-amber-300/30 bg-white/[0.03] p-4 space-y-2">
      <h2 className="text-sm font-semibold">Write to a staff member</h2>
      <select value={f.staff_id} onChange={(e) => setF({ ...f, staff_id: e.target.value })} data-testid="portal-memo-new-staff" className={field}>
        <option value="">Choose a staff member…</option>
        {staff.map((s) => <option key={s.id} value={s.id}>{s.name}{s.title ? ` · ${s.title}` : ""}</option>)}
      </select>
      <input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Subject" maxLength={200} data-testid="portal-memo-new-subject" className={field} />
      <textarea value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} placeholder="Your message" rows={4} data-testid="portal-memo-new-body" className={field} />
      <MemoAssist value={f.body} onChange={(v) => setF((x) => ({ ...x, body: v }))} id="portal-memo-new" />
      <button type="button" disabled={busy || !f.staff_id || !f.subject.trim() || !f.body.trim()} onClick={send} data-testid="portal-memo-new-send"
        className="rounded-full bg-amber-300 text-black px-5 py-2 text-sm font-semibold disabled:opacity-40">{busy ? "Sending…" : "Send memo"}</button>
      {msg && <p data-testid="portal-memo-new-msg" className={`text-xs ${msg.ok ? "text-emerald-300" : "text-red-300"}`}>{msg.t}</p>}
    </section>
  );
}

export default function PortalMemos({ token }) {
  const [memos, setMemos] = useState(null);
  const load = () => axios.get(`${API}/portal/memos`, { params: { token } }).then((r) => setMemos(r.data.memos)).catch(() => setMemos([]));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <section data-testid="portal-memos" className="space-y-3">
      <NewMemo token={token} onSent={load} />
      {memos?.length > 0 && <h2 className="text-lg font-semibold">Your messages</h2>}
      {(memos || []).map((m) => <PortalMemo key={m.id} m={m} token={token} onDone={load} />)}
    </section>
  );
}
